import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["scroller", "leftButton", "rightButton", "leftShadow", "rightShadow"]
  static values = { edgeGap: { type: Number, default: 32 } }

  connect() {
    if (!this.hasScrollerTarget) return

    this.update = this.update.bind(this)
    this.scrollerTarget.addEventListener("scroll", this.update, { passive: true })

    this.verticalScroller = this.findVerticalScroller()
    if (this.verticalScroller && this.verticalScroller !== window) {
      this.verticalScroller.addEventListener("scroll", this.update, { passive: true })
    }
    window.addEventListener("scroll", this.update, { passive: true })
    window.addEventListener("resize", this.update)

    this.resizeObserver = new ResizeObserver(this.update)
    this.resizeObserver.observe(this.scrollerTarget)
    const table = this.scrollerTarget.querySelector("table")
    if (table) this.resizeObserver.observe(table)

    requestAnimationFrame(this.update)
  }

  disconnect() {
    if (!this.hasScrollerTarget) return
    this.scrollerTarget.removeEventListener("scroll", this.update)
    if (this.verticalScroller && this.verticalScroller !== window) {
      this.verticalScroller.removeEventListener("scroll", this.update)
    }
    window.removeEventListener("scroll", this.update)
    window.removeEventListener("resize", this.update)
    this.resizeObserver?.disconnect()
  }

  findVerticalScroller() {
    let element = this.element.parentElement
    while (element && element !== document.body) {
      const overflowY = window.getComputedStyle(element).overflowY
      if (overflowY === "auto" || overflowY === "scroll") return element
      element = element.parentElement
    }
    return window
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.scrollerTarget

    const canScrollLeft = scrollLeft > 0
    const canScrollRight = scrollLeft + clientWidth < scrollWidth - 1

    this.toggleVisible(this.leftButtonTarget, canScrollLeft, this.hasLeftButtonTarget)
    this.toggleVisible(this.rightButtonTarget, canScrollRight, this.hasRightButtonTarget)
    this.toggleVisible(this.leftShadowTarget, canScrollLeft, this.hasLeftShadowTarget)
    this.toggleVisible(this.rightShadowTarget, canScrollRight, this.hasRightShadowTarget)

    this.positionDecorations()
  }

  toggleVisible(node, visible, exists) {
    if (!exists) return
    node.classList.toggle("hidden", !visible)
  }

  positionDecorations() {
    const gap = this.edgeGapValue
    const scroller = this.scrollerTarget

    const stickyRight = scroller.querySelector("tbody tr:not(.table-row--pinned) .table-column--actions")
    const stickyRightWidth = stickyRight ? stickyRight.getBoundingClientRect().width : 0

    const stickyLeftLast = scroller.querySelector("tbody tr:not(.table-row--pinned) .table-sticky-cell-last")
    const scrollerRect = scroller.getBoundingClientRect()
    const stickyLeftWidth = stickyLeftLast
      ? stickyLeftLast.getBoundingClientRect().right - scrollerRect.left
      : 0

    const horizontalScrollbarHeight = scroller.offsetHeight - scroller.clientHeight
    const verticalScrollbarWidth = scroller.offsetWidth - scroller.clientWidth

    if (this.hasRightShadowTarget) {
      this.rightShadowTarget.style.right = `${stickyRightWidth + verticalScrollbarWidth}px`
      this.rightShadowTarget.style.bottom = `${horizontalScrollbarHeight}px`
    }
    if (this.hasLeftShadowTarget) {
      this.leftShadowTarget.style.left = `${stickyLeftWidth}px`
      this.leftShadowTarget.style.bottom = `${horizontalScrollbarHeight}px`
    }

    const buttonTop = this.computeVisibleVerticalCenter()
    if (this.hasRightButtonTarget) {
      this.rightButtonTarget.style.right = `${stickyRightWidth + verticalScrollbarWidth + gap}px`
      this.rightButtonTarget.style.top = `${buttonTop}px`
    }
    if (this.hasLeftButtonTarget) {
      this.leftButtonTarget.style.left = `${stickyLeftWidth + gap}px`
      this.leftButtonTarget.style.top = `${buttonTop}px`
    }
  }

  computeVisibleVerticalCenter() {
    const frameRect = this.element.getBoundingClientRect()
    const horizontalScrollbarHeight = this.scrollerTarget.offsetHeight - this.scrollerTarget.clientHeight

    let viewportTop, viewportHeight
    if (this.verticalScroller === window) {
      viewportTop = 0
      viewportHeight = window.innerHeight
    } else {
      const rect = this.verticalScroller.getBoundingClientRect()
      viewportTop = rect.top
      viewportHeight = rect.height
    }

    const viewportCenter = viewportTop + viewportHeight / 2
    const frameRelativeCenter = viewportCenter - frameRect.top
    const halfButton = 14
    const minTop = halfButton
    const maxTop = frameRect.height - horizontalScrollbarHeight - halfButton
    return Math.max(minTop, Math.min(maxTop, frameRelativeCenter))
  }

  scrollLeft() {
    this.scrollerTarget.scrollBy({ left: -this.scrollerTarget.clientWidth * 0.7, behavior: "smooth" })
  }

  scrollRight() {
    this.scrollerTarget.scrollBy({ left: this.scrollerTarget.clientWidth * 0.7, behavior: "smooth" })
  }
}
